// Simple cross-domain HTTP client using native fetch
// This completely bypasses Vue's interceptor system

import configs from '@/config'

// Set of allowed cross-domain hosts - dynamically populated
const ALLOWED_CROSS_DOMAIN_HOSTS = new Set()

// Initialize allowed domains from config
if (configs.publicLanggraphApiUrl) {
  const publicLanggraphApiUrl = new URL(configs.publicLanggraphApiUrl)
  ALLOWED_CROSS_DOMAIN_HOSTS.add(publicLanggraphApiUrl.host)
}

if (configs.publicDocumentSystemApiUrl) {
  const publicDocumentSystemApiUrl = new URL(configs.publicDocumentSystemApiUrl)
  ALLOWED_CROSS_DOMAIN_HOSTS.add(publicDocumentSystemApiUrl.host)
}
if (configs.publicLanggraphApiUrl && configs.aiInsightsApiUrl) {
  const aiInsightsApiUrl = new URL(configs.aiInsightsApiUrl)
  ALLOWED_CROSS_DOMAIN_HOSTS.add(aiInsightsApiUrl.host)
}

/**
 * Cross-domain HTTP client that bypasses interceptors
 * Use this for uploading FormData to external domains
 */
class CrossDomainHttpClient {
  /**
   * Check if a URL is allowed for cross-domain requests
   * @param {string} url - The URL to check
   * @returns {boolean} - Whether the URL is allowed
   */
  static isAllowedDomain(url) {
    try {
      const urlObj = new URL(url)
      return ALLOWED_CROSS_DOMAIN_HOSTS.has(urlObj.host)
    } catch {
      return false
    }
  }

  /**
   * Send a POST request to an allowed cross-domain URL
   * Requires the target server to have CORS headers configured properly
   * @param {string} url - The target URL
   * @param {FormData|Object} data - The FormData or JSON data to send
   * @param {Object} headers - Optional headers to include
   * @returns {Promise} - Promise that resolves to response data
   */
  static async post(url, data, headers = {}) {
    if (!this.isAllowedDomain(url)) {
      throw new Error(`Domain not allowed for cross-domain requests: ${url}`)
    }

    try {
      const requestOptions = {
        method: 'POST',
        headers,
      }

      // Handle FormData vs JSON
      if (data instanceof FormData) {
        requestOptions.body = data
      } else {
        requestOptions.body = JSON.stringify(data)
        requestOptions.headers = {
          'Content-Type': 'application/json',
          ...headers,
        }
      }

      const response = await fetch(url, requestOptions)

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`)
        error.status = response.status
        error.statusText = response.statusText
        try {
          error.body = await response.json()
        } catch {
          // Fallback to text if response is not JSON
          error.body = { message: await response.text() }
        }
        throw error
      }

      const responseData = await response.json()

      // Return in Vue Resource format for compatibility
      return {
        body: responseData,
        status: response.status,
        statusText: response.statusText,
      }
    } catch (error) {
      // Convert to Vue Resource error format
      const vueResourceError = new Error(error.message)
      vueResourceError.body = error.body || { message: error.message }
      vueResourceError.status = error.status || 0
      vueResourceError.statusText = error.statusText || error.message
      throw vueResourceError
    }
  }

  /**
   * Send a GET request to an allowed cross-domain URL
   * @param {string} url - The target URL
   * @param {Object} params - Query parameters to append to the URL
   * @param {Object} headers - Optional headers to include
   * @param {Object} [options] - Optional request options
   * @param {AbortSignal} [options.signal] - AbortSignal to cancel the request
   * @returns {Promise} - Promise that resolves to response data
   */
  static async get(url, params = {}, headers = {}, { signal } = {}) {
    if (!this.isAllowedDomain(url)) {
      throw new Error(`Domain not allowed for cross-domain requests: ${url}`)
    }

    try {
      const urlObj = new URL(url)
      Object.keys(params).forEach((key) => urlObj.searchParams.append(key, params[key]))

      const requestOptions = {
        method: 'GET',
        headers,
      }
      if (signal) {
        requestOptions.signal = signal
      }

      const response = await fetch(urlObj.toString(), requestOptions)

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`)
        error.status = response.status
        error.statusText = response.statusText
        try {
          error.body = await response.json()
        } catch {
          error.body = { message: await response.text() }
        }
        throw error
      }

      const responseData = await response.json()

      return {
        body: responseData,
        status: response.status,
        statusText: response.statusText,
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error
      }
      const vueResourceError = new Error(error.message)
      vueResourceError.body = error.body || { message: error.message }
      vueResourceError.status = error.status || 0
      vueResourceError.statusText = error.statusText || error.message
      throw vueResourceError
    }
  }

  /**
   * Add a new allowed domain
   * @param {string} host - The host to allow (e.g., 'api.example.com:8080')
   */
  static addAllowedDomain(host) {
    ALLOWED_CROSS_DOMAIN_HOSTS.add(host)
  }
}

export default CrossDomainHttpClient
